import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ExternalLink, Instagram } from "lucide-react";
import { SparkleIcon } from "../components/BrandIcons";
import { useContent } from "../context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("All");
  const { content } = useContent();
  const vp = content.vendors_page || {};

  useEffect(() => {
    axios.get(`${API}/vendors`).then((r) => setVendors(r.data)).catch(() => {});
    axios.get(`${API}/vendor-categories`).then((r) => setCats(r.data || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...cats.map((c) => c.name)], [cats]);
  const filtered = useMemo(() => filter === "All" ? vendors : vendors.filter((v) => v.category === filter), [vendors, filter]);

  return (
    <div data-testid="vendors-page">
      <section className="grain-coral border-b-2 border-ink relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 md:py-24 md:px-10">
          <p className="eyebrow text-ink text-xs">{vp.eyebrow || "Vendors I love"}</p>
          <h1 className="heading mt-3 text-4xl md:text-6xl text-ink">{vp.headline || "My little black book."}</h1>
          <p className="mt-5 max-w-2xl text-lg text-ink/90">{vp.intro || "Hand-picked Melbourne creatives I’d send my best friend to. No commissions, no kickbacks — just genuinely brilliant people."}</p>
        </div>
      </section>

      <section className="bg-cream border-b-2 border-ink">
        <div className="mx-auto max-w-7xl px-5 py-8 md:px-10">
          <div className="flex flex-wrap gap-3">
            {categories.filter(Boolean).map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`eyebrow text-[0.7rem] px-4 py-2 border-2 border-ink transition-all ${filter === c ? 'bg-ink text-cream shadow-brutSm' : 'bg-cream text-ink hover:bg-coral'}`}
                data-testid={`vendor-filter-${(c||'').toLowerCase().replace(/\s|&/g,'')}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-12 md:py-16 md:px-10">
          {filtered.length === 0 ? (
            <p className="text-center text-ink/70 py-20">{vp.empty_message || "More vendors coming soon."}</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((v) => (
                <article key={v.id} className="card-brut bg-cream overflow-hidden flex flex-col" data-testid={`vendor-card-${v.id}`}>
                  {v.image && (
                    <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-ink bg-pinkpanel">
                      <img src={v.image} alt={v.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <span className="eyebrow text-[0.65rem] text-ink/70">{v.category}</span>
                    <h3 className="heading mt-2 text-2xl text-ink">{v.name}</h3>
                    <p className="mt-3 text-ink/90 flex-1">{v.description}</p>
                    <div className="mt-5 flex gap-3 flex-wrap">
                      {v.website && (
                        <a href={v.website} target="_blank" rel="noreferrer" className="btn-brut" data-testid={`vendor-website-${v.id}`}>
                          Website <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      {v.instagram && (
                        <a href={v.instagram} target="_blank" rel="noreferrer" className="btn-brut ghost" data-testid={`vendor-instagram-${v.id}`}>
                          <Instagram className="h-4 w-4" /> Instagram
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="grain-pink relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-5xl px-5 py-16 md:py-24 md:px-10 text-center">
          <SparkleIcon className="h-8 w-8 mx-auto text-ink" />
          <h2 className="heading mt-4 text-3xl md:text-5xl text-ink">{vp.final_headline || "Know someone who belongs here?"}</h2>
          <p className="mt-3 text-ink/90 text-lg">{vp.final_paragraph || "I’m always meeting new talent. Send their details through the contact form."}</p>
        </div>
      </section>
    </div>
  );
}
